<template>
    <main class="p-5 bg-echoes-background-black">
        <!-- Header Section -->
        <AdminHeader>
            <template #title>
                {{ props.title }}
            </template>
            <template #description>
                {{ props.description }}
            </template>
        </AdminHeader>

        <!-- Stats Cards -->
        <div class="mt-6 grid grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="uil uil-file-alt text-2xl text-sky-500"></i>
                </template>
                <template #title>Total {{ props.title }}</template>
                <template #value>{{ listingsCount }}</template>
            </StatCard>
            
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-star text-2xl text-amber-500"></i>
                </template>
                <template #title>Featured {{ props.title }}</template>
                <template #value>{{ featuredListingsCount }}</template>
            </StatCard>
            
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-crown text-2xl text-purple-500"></i>
                </template>
                <template #title>Premium {{ props.title }}</template>
                <template #value>{{ premiumListingsCount }}</template>
            </StatCard>
        </div>

        <!-- Main Content -->
        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96 flex items-center gap-2">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by listing name" 
                                class="w-full"
                            />
                        </div>
                        <div class="flex items-center gap-4">
                            <div class="w-full sm:w-24">
                                <SelectInput
                                    v-model="itemsPerPage"
                                    :options="itemsPerPageOptions"
                                    :hide-search="true"
                                    @update:modelValue="handleItemsPerPageChange"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div>
                    <table class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Listing Details
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Dataset Details
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Owner
                                </th>
                                <th scope="col" class="px-6 py-3 text-start text-xs font-medium text-white uppercase tracking-wider">
                                Created At 
                               </th>
                                <th scope="col" class="px-6 py-3 text-start text-xs font-medium text-white uppercase tracking-wider" v-if="props.type === 'rfp'">
                                    Expires At
                                </th>
                                <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-if="isLoading">
                                <tr v-for="i in 4" :key="i" class="bg-echoes-background-black/80 animate-pulse">
                                    <td class="px-2 py-4">
                                        <div class="flex items-center">
                                            <div class="ml-4">
                                                <div class="h-4 bg-echoes-background-gray rounded-full w-32 mb-2"></div>
                                                <div class="h-3 bg-echoes-background-gray rounded-full w-24"></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-2 py-4">
                                        <div class="flex items-center">
                                            <div class="ml-4">
                                                <div class="h-4 bg-echoes-background-gray rounded-full w-32 mb-2"></div>
                                                <div class="h-3 bg-echoes-background-gray rounded-full w-24"></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-full bg-echoes-background-gray"></div>
                                            </div>
                                            <div class="ml-4">
                                                <div class="h-4 bg-echoes-background-gray rounded-full w-24 mb-2"></div>
                                                <div class="h-3 bg-echoes-background-gray rounded-full w-20"></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="h-6 bg-echoes-background-gray rounded-full w-20"></div>
                                    </td>
                                    <td class="px-6 py-4" v-if="props.type === 'rfp'">
                                        <div class="h-6 bg-echoes-background-gray rounded-full w-20"></div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center space-x-4">
                                            <div class="h-5 w-5 bg-echoes-background-gray rounded"></div>
                                            <div class="h-5 w-5 bg-echoes-background-gray rounded"></div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-for="listing in listings" :key="listing.id" v-else>
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-2 py-4">
                                        <div class="flex items-center">
                                            <div class="ml-4">
                                                <div class="text-sm font-medium text-white" :title="listing.name">
                                                    {{ truncateText(listing.name || 'Not Available', 25) }}
                                                    <i class="mdi mdi-star text-amber-500 px-1" title="Featured" v-if="listing.featured"></i>
                                                    <i class="mdi mdi-hand-coin text-echoes-primary px-1" title="Accept Credits" v-if="listing.accept_credits"></i>
                                                    <i class="mdi mdi-crown text-purple-500 px-1" title="Premium" v-if="listing.premium"></i>
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ formatPrice(listing.price) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-2 py-4">
                                        <div class="flex items-center">
                                            <div class="ml-4" v-if="getDatasetInfo(listing)?.name">
                                                <div class="text-sm font-medium text-white" :title="getDatasetInfo(listing)?.name">
                                                    {{ truncateText(getDatasetInfo(listing)?.name, 20) }}
                                                </div>
                                                <div class="text-sm">
                                                    <span class="font-medium text-white/80">
                                                        {{ getDatasetInfo(listing)?.file_name || 'No file name' }} - 
                                                    </span>
                                                    <span class="text-sm text-white/80">
                                                        {{ formatFileSize(getDatasetInfo(listing)?.file_size ?? 0) }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="ml-4" v-else>
                                                <div class="text-sm font-medium text-white/50">
                                                    No dataset defined yet
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-full bg-echoes-primary flex items-center justify-center">
                                                    <span class="text-lg font-medium text-white">
                                                        <img v-if="getOwner(listing)?.profile_pic" :src="getOwner(listing).profile_pic" alt="user-avatar" class="h-10 w-10 rounded-full">
                                                        <span v-else class="text-lg font-medium text-white">
                                                            {{ getOwner(listing)?.username?.charAt(0)?.toUpperCase() || '' }}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-medium text-white">
                                                    {{ getOwner(listing)?.username || 'Untitled Listing' }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ getOwner(listing)?.user_group?.group?.name || 'Untitled Listing' }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex flex-col justify-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ formatDate(listing.created_at) }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                at {{ formatTime(listing.created_at) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4" v-if="props.type === 'rfp'">
                                        <div class="flex flex-col justify-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ formatDate(listing.expire_at) }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                at {{ formatTime(listing.expire_at) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center space-x-4">
                                            <button 
                                                @click="handleEdit(listing.id)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light">
                                            
                                            <i class="mdi mdi-pencil mr-2"></i>
                                            Edit
                                            </button>
                                                   
                                            <button 
                                                @click="handleView(listing.id)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light">
                                            <i class="mdi mdi-eye mr-2"></i>
                                            View
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-xl border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, listingsCount) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ listingsCount }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="handlePageChange(currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="handlePageChange(page)"
                                            :class="[
                                                'px-3 py-1 rounded-md text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="handlePageChange(currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { ref, onMounted, watch, defineProps, defineEmits, computed } from 'vue';
import axios from '@/plugins/axios';
import AdminHeader from '../AdminHeader.vue';
import { useLayoutComponents } from '@/composables/layout'; 
import StatCard from '@/pages/admin/StatCard.vue';
import { fetchListings } from '@/services/api';

const emit = defineEmits(['edit', 'view']);
const props = defineProps({
    title: {
        type: String,
        default: 'Listings'
    },
    description: {
        type: String,
        default: 'Manage all listings on the platform'
    },
    type: {
        type: String,
        default: 'buy_only'
    },
    editable: {
        type: Boolean,
        default: false
    }
});

const { TextInput, SelectInput } = useLayoutComponents();


const listings = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(12);
const searchQuery = ref('');
const listingsCount = ref(0);
const isLoading = ref(false);

const itemsPerPageOptions = [
    { value: 6, label: '6' },
    { value: 12, label: '12' },
    { value: 24, label: '24' },
    { value: 48, label: '48' },
    { value: 96, label: '96' },
];

const totalPages = computed(() => {
    return Math.ceil(listingsCount.value / itemsPerPage.value);
});

const handleItemsPerPageChange = () => {
    currentPage.value = 1;
    fetchListingsData();
};

const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchListingsData();
    }
};

const handleSearch = () => {
    currentPage.value = 1;
    fetchListingsData();
};

const handleEdit = (id) => {
    emit('edit', id);
};

const handleView = (id) => {
    emit('view', id);
};

const fetchListingsData = async () => {
    isLoading.value = true;
    const type = props.type;
    const endpoint = type === 'rfp' ? 'api/listings/user_rfps' : 'api/listings/all';
    const skip = (currentPage.value - 1) * itemsPerPage.value;
    const take = itemsPerPage.value;
    const isEval = true;
    
    try {
        const isRfp = type === 'rfp';
        const getType = isRfp ? `&type=rfp` : '';
        const response = await axios.get(`${endpoint}?skip=${skip}&take=${take}&search=${searchQuery.value}${getType}`);
        const evalResponse = await fetchListings(skip, take, isEval);
        if(response.status === 200 || response.status === 201 || evalResponse.status === 200 || evalResponse.status === 201) {
            const combinedListings = [...response.data.listings, ...evalResponse].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
            const uniqueListingsMap = new Map();
            combinedListings.forEach(listing => {
                if (listing.id && !uniqueListingsMap.has(listing.id)) {
                    uniqueListingsMap.set(listing.id, listing);
                }
            });
            listings.value = Array.from(uniqueListingsMap.values());
            listingsCount.value = listingsCount.value === 0 ? response.data.count : listingsCount.value;
            isLoading.value = false;
        }
    } catch (error) {
        console.error('Error fetching listings:', error);
        isLoading.value = false;
    }
};

const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = Number(price);
    // If the number is an integer, display it with commas and no decimals
    if (Number.isInteger(numPrice)) {
        return numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' USDC';
    }
    // Otherwise, display with two decimal places
    return numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USDC';
};

const getOwner = (listing) => {
    return listing.type === 'rfp' ? listing.applicant : listing.datasets?.[0]?.dataset?.seller?.user;
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}

const getDatasetInfo = (listing) => {
    return listing.type === 'rfp' ? listing.datasets?.[0] : listing.datasets?.[0]?.dataset;
}

const featuredListingsCount = computed(() => {
    return listings.value.filter(listing => listing.featured).length;
});

const premiumListingsCount = computed(() => {
    return listings.value.filter(listing => listing.premium).length;
});

watch(searchQuery, (newVal) => {
    if(newVal.length >= 3 || newVal.length === 0) {
        handleSearch();
    }
});

onMounted(() => {
    fetchListingsData();
});
</script>

<style scoped lang="postcss">
/* Custom Scrollbar */
::-webkit-scrollbar {
    @apply w-2;
}

::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

/* Stats Cards Hover Effect */
.bg-white.dark\:bg-slate-700 {
    @apply transition-all duration-200 hover:shadow-lg hover:scale-[1.02];
}
</style>